import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '@/contexts/CompanyContext';
import { graphqlQuery } from '@/lib/api/graphql';
import {
  mapTemplateDetailApiToUi,
  mapTemplateListItemApiToUi,
  mapTemplateUiToCreateRequest,
  mapTemplateUiToUpdateRequest,
} from '@/lib/mappers/templates';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import { useMode } from '@/providers/ModeProvider';
import type {
  CopyTemplateToGroupsApiResponse,
  CreateTemplateApiRequest,
  GetTemplatesApiResponse,
  TemplateCopyTargetApi,
  TemplateDetailApi,
  TemplateOverrideAvailabilityApi,
  UpdateTemplateApiRequest,
} from '@/types/api/templates';
import { mockTemplateCategoryTypes, mockTemplates } from '@/lib/demo-data/templates';
import type { Template } from '@/types/template';
import {
  clearDefaultTemplateDemoResponse,
  copyTemplateToGroupsDemoResponse,
  createTemplateDemoResponse,
  createTemplateOverrideDemoResponse,
  templateCopyTargetsDemoData,
  templateOverrideAvailabilityDemoData,
  makeUpdateTemplateDemoFn,
  setDefaultTemplateDemoResponse,
} from '@/services/demo/templates-demo.service';
import { useApi } from './useApi';
import { useModeAwareMutation, useModeAwareQuery } from './useModeAwareQuery';

export interface GetTemplatesParams extends Record<string, string | number | boolean | undefined> {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: string;
}

export function useTemplatesList() {
  const { apiGet } = useApi();
  const { selectedCompany } = useCompany();
  const { isDemo } = useMode();

  const fixedParams: GetTemplatesParams = {
    page: 1,
    pageSize: 100,
    sortBy: 'Name',
    sortOrder: 'asc',
  };

  return useModeAwareQuery<Template[], Error>(
    ['templates', selectedCompany?.id],
    async ({ signal }) => {
      const response = await apiGet<GetTemplatesApiResponse>('/api/v1/templates', fixedParams, signal);
      return response.data.map(mapTemplateListItemApiToUi);
    },
    mockTemplates,
    {
      staleTime: 0,
      refetchOnMount: 'always',
      enabled: isDemo || !!selectedCompany?.id,
    },
  );
}

export function useTemplateById(templateId: string | null) {
  const { apiGet } = useApi();
  const mockTemplate = mockTemplates.find((t) => t.id === templateId) ?? null;

  return useModeAwareQuery<Template | null, Error>(
    ['template', templateId],
    async ({ signal }) => {
      if (!templateId) return null;
      const response = await apiGet<TemplateDetailApi>(`/api/v1/templates/${templateId}`, undefined, signal);
      return mapTemplateDetailApiToUi(response);
    },
    mockTemplate,
    {
      enabled: !!templateId,
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}

/**
 * Fetches full template detail (including pdfSettings) for a set of template ids.
 * Used by the Send Statements flow to resolve a per-customer statement template.
 * Returns a map keyed by template id. Demo mode resolves from the mock templates.
 */
export function useStatementTemplatesByIds(templateIds: readonly string[]) {
  const { apiGet } = useApi();
  const { isDemo } = useMode();
  const uniqueIds = [...new Set(templateIds)].filter((id): id is string => !!id);

  const results = useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: ['template', id],
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const response = await apiGet<TemplateDetailApi>(`/api/v1/templates/${id}`, undefined, signal);
        return mapTemplateDetailApiToUi(response);
      },
      staleTime: 0,
      refetchOnMount: 'always' as const,
      enabled: !isDemo && !!id,
    })),
  });

  const templatesById = new Map<string, Template>();
  if (isDemo) {
    for (const id of uniqueIds) {
      const mockTemplate = mockTemplates.find((t) => t.id === id);
      if (mockTemplate) templatesById.set(id, mockTemplate);
    }
  } else {
    results.forEach((result, index) => {
      if (result.data) templatesById.set(uniqueIds[index], result.data);
    });
  }

  const isLoading = !isDemo && results.some((r) => r.isLoading);

  return { templatesById, isLoading };
}

export function useCreateTemplate({ forceLive = false }: { forceLive?: boolean } = {}) {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<TemplateDetailApi, Error, Template>(
    async (data) => {
      const request: CreateTemplateApiRequest = mapTemplateUiToCreateRequest(data);
      return apiPost<TemplateDetailApi>('/api/v1/templates', request);
    },
    createTemplateDemoResponse,
    {
      forceLive,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['templates'] });
        queryClient.invalidateQueries({ queryKey: ['templates-graphql-list'] });
      },
    },
  );
}

export function useUpdateTemplate(templateId: string, { forceLive = false }: { forceLive?: boolean } = {}) {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<TemplateDetailApi, Error, Template>(
    async (data) => {
      const request: UpdateTemplateApiRequest = mapTemplateUiToUpdateRequest(data);
      return apiPut<TemplateDetailApi>(`/api/v1/templates/${templateId}`, request);
    },
    makeUpdateTemplateDemoFn(templateId),
    {
      forceLive,
      onSuccess: () => {
        queryClient.removeQueries({
          queryKey: ['template', templateId],
          exact: false,
        });
        queryClient.invalidateQueries({ queryKey: ['templates'] });
        queryClient.invalidateQueries({ queryKey: ['templates-graphql-list'] });
      },
    },
  );
}

export function useDeleteTemplate() {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<void, Error, string>(
    async (templateId) => {
      await apiDelete(`/api/v1/templates/${templateId}`);
    },
    async () => {
      return;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['templates'] });
        queryClient.invalidateQueries({ queryKey: ['templates-graphql-list'] });
      },
    },
  );
}

/** Creates this company's editable copy of a shared group-level template. */
export function useCreateTemplateOverride() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<TemplateDetailApi, Error, string>(
    async (templateId) => {
      return apiPost<TemplateDetailApi>(`/api/v1/templates/${templateId}/override`, {});
    },
    createTemplateOverrideDemoResponse,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['templates'] });
        queryClient.invalidateQueries({ queryKey: ['templates-graphql-list'] });
      },
    },
  );
}

export interface CopyTemplateToGroupsVariables {
  templateId: string;
  companyGroupIds: number[];
}

/** Company groups the current user can copy a shared template into, excluding the current one. */
export function useTemplateCopyTargets(enabled: boolean) {
  const { apiGet } = useApi();
  const { selectedCompany } = useCompany();
  const { isDemo } = useMode();

  return useModeAwareQuery<TemplateCopyTargetApi[], Error>(
    ['template-copy-targets', selectedCompany?.id],
    async ({ signal }) => apiGet<TemplateCopyTargetApi[]>('/api/v1/templates/copy-targets', {}, signal),
    templateCopyTargetsDemoData,
    { enabled: enabled && (isDemo || !!selectedCompany?.id) },
  );
}

/**
 * Whether customising a shared template for one company is meaningful in the current group.
 * A group with a single company has nothing to vary, so the action is not offered.
 */
export function useTemplateOverrideAvailability() {
  const { apiGet } = useApi();
  const { selectedCompany } = useCompany();
  const { isDemo } = useMode();

  return useModeAwareQuery<TemplateOverrideAvailabilityApi, Error>(
    ['template-override-availability', selectedCompany?.id],
    async ({ signal }) =>
      apiGet<TemplateOverrideAvailabilityApi>('/api/v1/templates/override-availability', {}, signal),
    templateOverrideAvailabilityDemoData,
    { enabled: isDemo || !!selectedCompany?.id },
  );
}

export function useCopyTemplateToGroups() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<CopyTemplateToGroupsApiResponse, Error, CopyTemplateToGroupsVariables>(
    async ({ templateId, companyGroupIds }) =>
      apiPost<CopyTemplateToGroupsApiResponse>(`/api/v1/templates/${templateId}/copy-to-groups`, {
        companyGroupIds,
      }),
    copyTemplateToGroupsDemoResponse,
    {
      onSuccess: () => {
        // The copies land in other groups, but the current list is refreshed for consistency.
        queryClient.invalidateQueries({ queryKey: ['templates'] });
        queryClient.invalidateQueries({ queryKey: ['templates-graphql-list'] });
      },
    },
  );
}

export function useSetDefaultTemplate() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<TemplateDetailApi, Error, string>(
    async (templateId) => {
      return apiPost<TemplateDetailApi>(`/api/v1/templates/${templateId}/set-default`, {});
    },
    setDefaultTemplateDemoResponse,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['templates'] });
        queryClient.invalidateQueries({ queryKey: ['templates-graphql-list'] });
      },
    },
  );
}

export function useClearDefaultTemplate() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<TemplateDetailApi, Error, string>(
    async (templateId) => {
      return apiPost<TemplateDetailApi>(`/api/v1/templates/${templateId}/clear-default`, {});
    },
    clearDefaultTemplateDemoResponse,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['templates'] });
        queryClient.invalidateQueries({ queryKey: ['templates-graphql-list'] });
      },
    },
  );
}

// ─── Template Category Types (GraphQL) ─────────────────────────────────────

export interface TemplateCategoryType {
  id: number;
  code: string;
  name: string;
  description?: string;
}

interface TemplateCategoryTypeNode {
  templateCategoryTypeId: number;
  code: string;
  name: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
}

interface TemplateCategoryTypeListGraphQLResponse {
  templateCategoryTypes: {
    templateCategoryTypeList: {
      edges: Array<{ node: TemplateCategoryTypeNode }>;
    };
  };
}

const TEMPLATE_CATEGORY_TYPE_LIST_QUERY = `
  query TemplateCategoryTypeList($first: Int) {
    templateCategoryTypes {
      templateCategoryTypeList(first: $first) {
        edges {
          node {
            templateCategoryTypeId
            code
            name
            description
            sortOrder
            isActive
          }
        }
      }
    }
  }
`;

function mapNodeToTemplateCategoryType(node: TemplateCategoryTypeNode): TemplateCategoryType {
  return {
    id: node.templateCategoryTypeId,
    code: node.code,
    name: node.name,
    description: node.description ?? undefined,
  };
}

export function useTemplateCategoryTypes() {
  const apiConfig = useApiConfig();

  return useModeAwareQuery<TemplateCategoryType[], Error>(
    ['templateCategoryTypes'],
    async ({ signal }) => {
      const response = await graphqlQuery<TemplateCategoryTypeListGraphQLResponse>(
        apiConfig,
        TEMPLATE_CATEGORY_TYPE_LIST_QUERY,
        { first: 0 },
        signal,
      );

      return response.templateCategoryTypes.templateCategoryTypeList.edges
        .map((x) => mapNodeToTemplateCategoryType(x.node))
        .filter((x) => x.code !== undefined);
    },
    mockTemplateCategoryTypes,
    {
      staleTime: 5 * 60 * 1000,
    },
  );
}
